import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, School, HeartHandshake, GraduationCap, Wallet, AlertTriangle, BookOpenCheck, ClipboardList } from 'lucide-react';
import { C, brl, num, pct } from './theme';

export const Panel = ({ title, kicker, children, testid, right, className = '' }) => (
  <section className={`min-w-0 rounded-[24px] border bg-white p-5 sm:p-6 ${className}`} style={{ borderColor: C.line }} data-testid={testid}>
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        {kicker && <p className="text-[10.5px] font-bold uppercase tracking-[0.22em]" style={{ color: C.purple }}>{kicker}</p>}
        <h2 className="text-base md:text-lg font-extrabold tracking-tight" style={{ color: C.ink, fontFamily: 'Manrope, sans-serif' }}>{title}</h2>
      </div>
      {right}
    </div>
    {children}
  </section>
);

export const Empty = ({ children, testid }) => (
  <p className="rounded-2xl border border-dashed py-6 text-center text-sm" style={{ borderColor: C.line, color: C.muted }} data-testid={testid}>{children}</p>
);

const Kpi = ({ id, label, value, hint, icon: Icon, color, path, i }) => {
  const navigate = useNavigate();
  return (
    <button type="button" onClick={() => path && navigate(path)} data-testid={`kpi-${id}`}
      className="group relative overflow-hidden rounded-[22px] border bg-white p-5 text-left transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_18px_40px_-24px_rgba(63,31,92,.45)] dash-rise"
      style={{ borderColor: C.line, animationDelay: `${i * 45}ms` }}>
      <span className="absolute -right-6 -top-6 h-20 w-20 rounded-full opacity-[.13] transition-transform duration-300 group-hover:scale-125" style={{ background: color }} />
      <span className="flex h-10 w-10 items-center justify-center rounded-2xl" style={{ background: `${color}18`, color }}><Icon className="h-5 w-5" /></span>
      <p className="mt-4 text-[28px] font-extrabold leading-none tracking-tight tabular-nums" style={{ color: C.ink, fontFamily: 'Manrope, sans-serif' }} data-testid={`kpi-${id}-value`}>{value}</p>
      <p className="mt-1.5 text-[13px] font-semibold" style={{ color: C.aubergine }}>{label}</p>
      {hint && <p className="mt-0.5 text-[11.5px]" style={{ color: C.muted }}>{hint}</p>}
    </button>
  );
};

export function KpiGrid({ k, fin, acad, year }) {
  const items = [
    { id: 'alunos', label: 'Alunos ativos', value: num(k.active_students), hint: `Ano letivo ${year}`, icon: Users, color: C.purple, path: '/v2/aluno' },
    { id: 'turmas', label: 'Turmas ativas', value: num(k.active_classes), hint: 'Turmas do ano', icon: School, color: C.green, path: '/v2/academico/turmas' },
    { id: 'responsaveis', label: 'Responsáveis vinculados', value: num(k.linked_guardians), hint: 'Sem duplicidade', icon: HeartHandshake, color: C.magenta, path: '/v2/aluno' },
    { id: 'professores', label: 'Professores ativos', value: num(k.active_teachers), hint: 'Com atribuição ativa', icon: GraduationCap, color: C.amber, path: '/v2/academico' },
    { id: 'receita', label: 'Receita recebida', value: brl(k.received), hint: `de ${brl(fin.expected)} previstos`, icon: Wallet, color: C.green, path: '/v2/financeiro/titulos' },
    { id: 'inadimplencia', label: 'Inadimplência real', value: pct(k.delinquency_pct), hint: `${brl(k.overdue)} vencidos`, icon: AlertTriangle, color: k.delinquency_pct >= 5 ? C.orange : C.lime, path: '/v2/financeiro/inadimplentes' },
    { id: 'notas', label: 'Notas pendentes', value: num(k.pending_grades), hint: acad.trimestre, icon: BookOpenCheck, color: C.purple, path: '/v2/academico/pendencias' },
    { id: 'pendencias', label: 'Pendências operacionais', value: num(k.pending_operations), hint: 'Documentos, assinaturas e mais', icon: ClipboardList, color: C.magenta, path: '/v2/central' },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8" data-testid="kpi-grid">
      {items.map((it, i) => <Kpi key={it.id} {...it} i={i} />)}
    </div>
  );
}
